import { useState } from 'react';
import { NavBar } from '../../components/NavBar.tsx';
import { Alert } from '../../components/Overlay.tsx';
import { Seg } from '../../components/Seg.tsx';
import { ErrorState } from '../../components/DataState.tsx';
import { ApiError } from '../../api/client.ts';
import { createNote } from '../../api/notes.ts';
import { CATEGORIES } from '../../domain.ts';
import type { CategoryName, PrayerNote } from '../../domain.ts';
import { today } from './shared.ts';

/**
 * C2 — 새 기도 노트.
 *
 * 받는 것은 제목 · 내용 · 영역 · PLAN · Private 다섯입니다. 기도 상태와
 * 중요도는 서버가 디폴트로 채우고, 대표 지정과 공유는 저장한 뒤 설정
 * 화면(C4)에서 합니다. 작성 화면에서 다 받으려 하면 첫 노트를 쓰는 일이
 * 무거워집니다.
 *
 * 영역을 여기서 고르는 것은 목록에서 들고 올 경로가 없어졌기 때문입니다 —
 * ＋ 는 화면 어디서 눌러도 같은 빈 노트를 엽니다.
 */

interface Props {
  author: string;
  onCancel: () => void;
  onSaved: (note: PrayerNote) => void;
}

export function NewNote({ author, onCancel, onSaved }: Props) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<CategoryName>(CATEGORIES[0]);
  const [body, setBody] = useState('');
  const [plan, setPlan] = useState<'EVERYDAY' | 'DATE'>('DATE');
  const [date, setDate] = useState(today());
  const [isPrivate, setPrivate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [confirmLeave, setConfirmLeave] = useState(false);

  const dirty = title.trim() !== '' || body.trim() !== '';

  /** 적은 것이 있으면 한 번 묻고 나갑니다. 비어 있으면 그냥 닫습니다 */
  const leave = () => (dirty ? setConfirmLeave(true) : onCancel());

  const save = async () => {
    setBusy(true);
    setFailure(null);
    try {
      onSaved(
        await createNote({
          title: title.trim(),
          body,
          category,
          plan: plan === 'EVERYDAY' ? { type: 'EVERYDAY' } : { type: 'DATE', date },
          isPrivate,
        }),
      );
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : '저장하지 못했습니다.');
      setBusy(false);
    }
  };

  return (
    <div className="screen">
      <NavBar
        title="새 기도 노트"
        subtitle={category}
        start={{ icon: 'close', label: '나가기', onClick: leave }}
        end={{ icon: 'check', label: '저장', accent: true, onClick: save }}
      />

      <div className="screen__body">
        {failure && <ErrorState message={failure} onRetry={() => setFailure(null)} />}

        <label className="field">
          <span className="field__label">제목</span>
          <input
            className="input"
            value={title}
            autoFocus
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">내용</span>
          <textarea
            className="input input--tall"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </label>

        {/* 여섯 중 하나. 나중에 설정 화면(C4)에서 옮길 수 있습니다 */}
        <div className="field">
          <span className="field__label">영역</span>
          <Seg
            label="영역"
            options={CATEGORIES.map((c) => ({ value: c, label: c }))}
            value={category}
            onChange={setCategory}
          />
        </div>

        <div className="field">
          <span className="field__label">Plan</span>
          <Seg
            label="Plan"
            options={[
              { value: 'EVERYDAY', label: 'Everyday' },
              { value: 'DATE', label: '날짜 지정' },
            ]}
            value={plan}
            onChange={setPlan}
          />
          {/* Everyday 를 고르면 날짜 칸이 사라집니다 — 달력에도 나타나지 않습니다 */}
          {plan === 'DATE' && (
            <input
              className="input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          )}
        </div>

        <div className="field">
          <span className="field__label">Private</span>
          <button
            type="button"
            className="toggle-row"
            aria-pressed={isPrivate}
            onClick={() => setPrivate((v) => !v)}
          >
            <span className="toggle-row__label">
              비공개
              <span className="toggle-row__sub">친구에게 공개 노트로 보이지 않습니다</span>
            </span>
            <span className={isPrivate ? 'toggle toggle--on' : 'toggle'} />
          </button>
        </div>

        {/* 자동으로 채워지는 값이라 입력할 수 없다는 것을 색으로 구분합니다 */}
        <div className="card card--flat">
          <div className="kv">
            <span className="kv__k">작성자</span>
            <span className="kv__v">{author}</span>
          </div>
          <div className="kv">
            <span className="kv__k">Created</span>
            <span className="kv__v">{today().replace(/-/g, '.')}</span>
          </div>
        </div>

        <p className="hint">
          기도 상태는 <b>기도중</b>, 중요도는 <b>일반</b>으로 저장됩니다. 대표 지정과 공유는 저장한
          뒤 설정 화면에서 합니다.
        </p>

        <div className="btn-row btn-row--foot">
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || title.trim() === ''}
            onClick={save}
          >
            {busy ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>

      {confirmLeave && (
        <Alert
          title="쓰던 것을 버릴까요?"
          onClose={() => setConfirmLeave(false)}
          actions={
            <>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setConfirmLeave(false)}
              >
                계속 쓰기
              </button>
              <button type="button" className="btn btn--danger" onClick={onCancel}>
                버리기
              </button>
            </>
          }
        >
          <p>저장하지 않은 내용은 사라집니다.</p>
        </Alert>
      )}
    </div>
  );
}
